
import { useState } from "react"
import { CheckIcon, ChevronDownIcon, Info } from "lucide-react"

import { buttonVariants } from "../Diwalipage/Button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../Diwalipage/Collapsible"
import { cn } from "../../lib/utils"

const VISIBLE_FEATURES = 6

/** Billing toggle + plan cards. Prices and features are passed in word for word from the site. */
export function PricingPlans({
  plans,
  copy,
  offerActive,
  formHref,
  onToggle,
  onPlanClick,
  highlight = [],
  highlightNote,
}) {
  const isMarked = (f) =>
    highlight.some((h) => f.toLowerCase().includes(h.toLowerCase()))

  const [billing, setBilling] = useState("yearly")

  const choose = (b) => {
    if (b === billing) return
    setBilling(b)
    onToggle(b)
  }

  const yearlyOffer = offerActive && billing === "yearly"

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col items-center gap-3">
        <div
          role="group"
          aria-label="Billing period"
          className="inline-flex rounded-lg border border-border bg-background p-1 shadow-card"
        >
          {copy.toggle.map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={billing === value}
              onClick={() => choose(value)}
              className={cn(
                "text-xs inline-flex min-h-11 items-center gap-2 rounded-md px-4 text-sm font-medium transition-colors sm:px-5 text-sm",
                billing === value
                  ? "bg-charcoal text-white"
                  : "text-ink-strong hover:bg-muted"
              )}
            >
              {label}

              {value === "yearly" && offerActive && (
                <span
                  className={cn(
                    "rounded px-1.5 py-0.5 text-[11px] font-semibold",
                    billing === value
                      ? "bg-brand text-white"
                      : "bg-campaign-tint text-campaign-text"
                  )}
                >
                  {copy.yearlyBadge}
                </span>
              )}
            </button>
          ))}
        </div>

        {highlightNote && (
          <p className="max-w-2xl rounded-lg bg-brand-tint px-3 py-1.5 text-center text-sm font-medium text-brand-text">
            {highlightNote}
          </p>
        )}

        {offerActive && billing !== "yearly" && (
          <button
            type="button"
            onClick={() => choose("yearly")}
            className="min-h-11 text-sm font-semibold text-brand-text underline underline-offset-2"
          >
            {copy.switchToYearly}
          </button>
        )}
      </div>

      <ul className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        {plans.map((plan) => {
          const p = plan.prices[billing]
          const offer = yearlyOffer ? plan.offer : undefined

          // Marked lines always stay visible; the rest collapse after VISIBLE_FEATURES.
          const visible = plan.features.filter(
            (f, i) => i < VISIBLE_FEATURES || isMarked(f)
          )

          const more = plan.features.filter(
            (f, i) => i >= VISIBLE_FEATURES && !isMarked(f)
          )

          return (
            <li
              key={plan.id}
              className={cn(
                "relative flex flex-col gap-5 rounded-lg bg-background p-6",
                plan.highlighted
                  ? "border-2 border-brand shadow-featured"
                  : "border border-border shadow-card"
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-xl font-semibold">{plan.name}</h3>

                {offer && (
                  <span className="shrink-0 rounded-md bg-campaign-tint px-2 py-1 text-xs font-semibold text-campaign-text">
                    {copy.offerTag}
                  </span>
                )}
              </div>

              <p className="-mt-2 text-sm text-muted-foreground">
                {plan.tagline}
              </p>

              <div aria-live="polite">
                {offer ? (
                  <>
                    <p className="text-sm text-muted-foreground line-through decoration-1">
                      <span className="sr-only">Regular yearly price: </span>
                      {p.price}
                      {p.per} · {p.note}
                    </p>

                    <p className="mt-1 flex items-baseline gap-1">
                      <span className="text-4xl font-semibold tracking-tight">
                        {offer.price}
                      </span>
                      <span className="text-muted-foreground">/mo</span>
                    </p>

                    <p className="mt-1 text-sm">{offer.billed}</p>
                    <p className="mt-1 text-sm font-semibold text-brand-text">
                      {offer.save}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="flex items-baseline gap-1">
                      <span className="text-4xl font-semibold tracking-tight">
                        {p.price}
                      </span>
                      {p.per && (
                        <span className="text-muted-foreground">{p.per}</span>
                      )}
                    </p>

                    <p className="mt-1 text-sm text-muted-foreground">
                      {p.note}
                    </p>
                  </>
                )}

                {plan.gst && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {copy.gstLine}
                  </p>
                )}
              </div>

              <a
                href={
                  plan.id === "free"
                    ? "https://app.onbbits.io/register"
                    : formHref
                }
                onClick={() => onPlanClick(plan.id, billing)}
                className={buttonVariants({
                  variant: plan.highlighted || offer ? "default" : "outline",
                  size: "lg",
                  className: "w-full",
                })}
              >
                {offer ? offer.button : plan.button}
              </a>

              <div className="flex flex-col gap-2.5">
                {plan.featuresHeading && (
                  <p className="text-sm font-semibold">
                    {plan.featuresHeading}
                  </p>
                )}

                <ul className="grid gap-2.5">
                  {visible.map((f) => (
                    <Feature key={f} text={f} marked={isMarked(f)} />
                  ))}
                </ul>

                {more.length > 0 && (
                  <Collapsible>
                    <CollapsibleContent>
                      <ul className="grid gap-2.5">
                        {more.map((f) => (
                          <Feature key={f} text={f} marked={isMarked(f)} />
                        ))}
                      </ul>
                    </CollapsibleContent>

                    <CollapsibleTrigger className="group/more mt-1 inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-brand-text hover:underline">
                      <span className="group-data-[panel-open]/more:hidden">
                        {copy.moreLabel} ({plan.features.length})
                      </span>

                      <span className="hidden group-data-[panel-open]/more:inline">
                        {copy.lessLabel}
                      </span>

                      <ChevronDownIcon
                        className="size-4 transition-transform group-data-[panel-open]/more:rotate-180"
                        aria-hidden="true"
                      />
                    </CollapsibleTrigger>
                  </Collapsible>
                )}
              </div>

              <div className="mt-auto rounded-lg bg-surface-soft p-3">
<a
    href="/message-template-charges.pdf"
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-2 text-xs font-semibold hover:text-brand-text"
  >
    {copy.ratesTitle}
    <Info className="size-4 shrink-0" aria-hidden="true" />
  </a>


                <dl className="mt-2 grid gap-y-1 text-xs">
                  {copy.rates.map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-2">
                      <dt className="text-muted-foreground">{k}</dt>
                      <dd className="font-semibold">{v}</dd>
                    </div>
                  ))}
                </dl>

                <p className="mt-2 text-xs text-ink-strong">
                  {copy.ratesFree}
                </p>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function Feature({ text, marked }) {
  return (
    <li
      className={cn(
        "flex items-start gap-2.5 text-sm",
        marked && "-mx-2 rounded-md bg-brand-tint px-2 py-1 font-semibold"
      )}
    >
      <CheckIcon
        className="mt-0.5 size-4 shrink-0 text-brand-text"
        aria-hidden="true"
      />
      {text}
    </li>
  )
}
